import { useEffect, useState } from "react";
import { toWorker } from "../util/toWorker.ts";
import { useDesktopStore } from "../store.ts";
import { formatRating, scoutPlayer } from "../util/scouting.ts";

type BoxLine = { pid: number; firstName: string; lastName: string; pos: string; pts: number; reb: number; ast: number; played: boolean };
type BoxScore = {
	id: number; dateString: string; phase: string;
	homeTid: number; awayTid: number;
	homeScore: number; awayScore: number;
	homeName: string; awayName: string;
	homeBox: BoxLine[]; awayBox: BoxLine[];
};

const BoxScoreModal = ({ box, tid, onClose }: { box: BoxScore; tid: number; onClose: () => void }) => {
	const isHome = box.homeTid === tid;
	const myBox = isHome ? box.homeBox : box.awayBox;
	const oppBox = isHome ? box.awayBox : box.homeBox;
	const myScore = isHome ? box.homeScore : box.awayScore;
	const oppScore = isHome ? box.awayScore : box.homeScore;
	const oppName = isHome ? box.awayName : box.homeName;
	const won = myScore > oppScore;

	return (
		<div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }}
			onClick={onClose}>
			<div style={{ background:"var(--bg-primary, #0e1117)", border:"1px solid var(--border)", borderRadius:10, padding:20, maxWidth:640, width:"95vw", maxHeight:"80vh", overflowY:"auto" }}
				onClick={(e) => e.stopPropagation()}>
				<div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
					<div>
						<span style={{ fontWeight:700, fontSize:16 }}>
							{isHome ? `${box.homeName}` : `${box.awayName}`}
						</span>
						{" "}
						<span style={{ color: won ? "var(--green)" : "var(--red)", fontWeight:700, fontSize:18 }}>{myScore}</span>
						{" – "}
						<span style={{ fontSize:14, color:"var(--muted)" }}>{oppName} {oppScore}</span>
						{" "}
						<span style={{ fontSize:12, color:"var(--muted)" }}>({box.dateString})</span>
					</div>
					<button type="button" className="btn btn-ghost" onClick={onClose}>✕</button>
				</div>

				<table className="data-table" style={{ marginBottom:12 }}>
					<thead><tr><th>Player</th><th>Pos</th><th className="num">PTS</th><th className="num">REB</th><th className="num">AST</th></tr></thead>
					<tbody>
						{myBox.map((p) => (
							<tr key={p.pid} style={{ opacity: p.played ? 1 : 0.45 }}>
								<td>{p.firstName} {p.lastName}</td>
								<td style={{ fontSize:"0.82em" }}>{p.pos}</td>
								<td className="num">{p.played ? p.pts : "DNP"}</td>
								<td className="num">{p.played ? p.reb : ""}</td>
								<td className="num">{p.played ? p.ast : ""}</td>
							</tr>
						))}
					</tbody>
				</table>

				<div style={{ fontSize:12, color:"var(--muted)", marginBottom:6 }}>Opponent: {oppName}</div>
				<table className="data-table">
					<thead><tr><th>Player</th><th>Pos</th><th className="num">PTS</th><th className="num">REB</th><th className="num">AST</th></tr></thead>
					<tbody>
						{oppBox.map((p) => (
							<tr key={p.pid} style={{ opacity: p.played ? 1 : 0.45 }}>
								<td>{p.firstName} {p.lastName}</td>
								<td style={{ fontSize:"0.82em" }}>{p.pos}</td>
								<td className="num">{p.played ? p.pts : "DNP"}</td>
								<td className="num">{p.played ? p.reb : ""}</td>
								<td className="num">{p.played ? p.ast : ""}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export const CollegeTeam = () => {
	const collegeTid = useDesktopStore((s) => s.collegeTid);
	const revision = useDesktopStore((s) => s.revision);
	const setView = useDesktopStore((s) => s.setView);
	const openProspect = useDesktopStore((s) => s.openProspect);
	const openPlayer = useDesktopStore((s) => s.openPlayer);
	const lid = useDesktopStore((s) => s.lid);
	const scouting = useDesktopStore((s) => s.scouting);
	const setScouting = useDesktopStore((s) => s.setScouting);
	const pushToast = useDesktopStore((s) => s.pushToast);
	const readOnly = useDesktopStore((s) => s.readOnly);
	const [data, setData] = useState<any>();
	const [boxScores, setBoxScores] = useState<BoxScore[]>([]);
	const [openBox, setOpenBox] = useState<BoxScore | null>(null);

	useEffect(() => {
		if (collegeTid == null) {
			return;
		}
		void toWorker("main", "getCollegeTeamDetail", collegeTid)
			.then(setData)
			.catch(console.error);
		void toWorker("main", "getCollegeBoxScores", collegeTid)
			.then((bs: any) => setBoxScores(bs ?? []))
			.catch(console.error);
	}, [collegeTid, revision]);

	if (collegeTid == null) {
		return (
			<div className="empty">
				No school selected.{" "}
				<button
					type="button"
					className="btn btn-primary"
					onClick={() => setView("college")}
				>
					Back to College
				</button>
			</div>
		);
	}

	const team = data?.team;
	const players = data?.players ?? [];
	const futureCommits = data?.futureCommits ?? [];

	return (
		<>
			<button
				type="button"
				className="btn btn-ghost"
				style={{ marginBottom: 12 }}
				onClick={() => setView("college")}
			>
				← Conferences
			</button>
			<h1 className="page-title">
				{team ? `${team.region} ${team.name}` : "College Team"}
			</h1>
			<p className="page-sub">
				{data?.conf?.name ?? ""} · Team Overall {data?.teamOvr ?? "—"} ·
				Prestige {data?.prestige ?? "—"} · Record{" "}
				{data ? `${data.won}-${data.lost}` : "—"}
			</p>

			<section className="panel" style={{ marginBottom: 14 }}>
				<div className="panel-header">
					<span>Roster</span>
					<span className="pill">{players.length} listed</span>
				</div>
				<div className="panel-body" style={{ padding: 0 }}>
					<table className="data-table">
						<thead>
							<tr>
								<th>Yr</th>
								<th>Pos</th>
								<th>Player</th>
								<th className="num">Ovr</th>
								<th className="num">GP</th>
								<th className="num">PPG</th>
								<th className="num">RPG</th>
								<th className="num">APG</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{players.map((p: any) => (
								<tr
									key={p.pid}
									style={
										p.status === "declared"
											? { opacity: 0.72 }
											: undefined
									}
								>
									<td>{p.year}</td>
									<td>{p.pos}</td>
									<td>
										<button
											type="button"
											className="player-link"
											onClick={() => {
												if (p.proPid != null) {
													openPlayer(p.proPid);
												} else {
													openProspect({
														kind: "college",
														pid: p.pid,
														tid: collegeTid,
													});
												}
											}}
										>
											{p.firstName} {p.lastName}
										</button>
										{p.status === "declared" ? (
											<span className="pill" style={{ marginLeft: 6 }}>
												Declared
											</span>
										) : null}
										{p.hsRank != null ? (
											<span className="pill" style={{ marginLeft: 6 }}>
												HS #{p.hsRank}
											</span>
										) : null}
									</td>
									<td className="num">
										{formatRating(scouting, p.pid, p.ovr, "ovr")}
									</td>
									<td className="num">{p.stats?.gp ?? 0}</td>
									<td className="num">{p.ppg}</td>
									<td className="num">{p.rpg}</td>
									<td className="num">{p.apg}</td>
									<td>
										{!readOnly ? (
											<button
												type="button"
												className="btn btn-ghost"
												style={{
													minHeight: 28,
													fontSize: 12,
													padding: "4px 8px",
												}}
												onClick={() => {
													if (lid == null) {
														return;
													}
													const result = scoutPlayer(
														lid,
														scouting,
														p.pid,
														"college",
													);
													setScouting(result.state);
													pushToast({
														text: result.message,
														type: result.ok ? "info" : "error",
													});
												}}
											>
												Scout
											</button>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>

		<section className="panel">
			<div className="panel-header">
				<span>Future Commits</span>
				<span className="pill">{futureCommits.length} signed</span>
			</div>
			<div className="panel-body" style={{ padding: 0 }}>
				{futureCommits.length === 0 ? (
					<div className="empty" style={{ padding: 20 }}>
						No high-school commits yet. As Top 100 prospects sign, they
						appear here and enroll as freshmen next season.
					</div>
				) : (
					<table className="data-table">
						<thead>
							<tr>
								<th className="num">HS#</th>
								<th>Pos</th>
								<th>Player</th>
								<th className="num">Ovr</th>
								<th>Arrives</th>
							</tr>
						</thead>
						<tbody>
							{futureCommits.map((p: any) => (
								<tr key={p.pid}>
									<td className="num">{p.hsRank ?? "—"}</td>
									<td>{p.pos}</td>
									<td>
										<button
											type="button"
											className="player-link"
											onClick={() =>
												openProspect({
													kind: "hs",
													pid: p.hsPid ?? p.pid,
												})
											}
										>
											{p.firstName} {p.lastName}
										</button>
									</td>
									<td className="num">
										{formatRating(scouting, p.pid, p.ovr, "ovr")}
									</td>
									<td>
										{p.arrivesSeason != null
											? `${p.arrivesSeason} FR`
											: "Next season"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</section>

		{boxScores.length > 0 ? (
			<section className="panel" style={{ marginTop: 14 }}>
				<div className="panel-header">
					<span>Recent Games</span>
					<span className="pill">{boxScores.length} games</span>
				</div>
				<div className="panel-body" style={{ padding: 0 }}>
					<table className="data-table">
						<thead>
							<tr>
								<th>Date</th>
								<th>Opponent</th>
								<th className="num">Score</th>
								<th>Result</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{boxScores.slice(0, 15).map((bs) => {
								const isHome = bs.homeTid === collegeTid;
								const myScore = isHome ? bs.homeScore : bs.awayScore;
								const oppScore = isHome ? bs.awayScore : bs.homeScore;
								const oppName = isHome ? bs.awayName : bs.homeName;
								const won = myScore > oppScore;
								return (
									<tr key={bs.id}>
										<td style={{ fontSize:"0.82em", color:"var(--muted)" }}>{bs.dateString}</td>
										<td style={{ fontSize:"0.9em" }}>{oppName}</td>
										<td className="num">{myScore}–{oppScore}</td>
										<td>
											<span style={{ color: won ? "var(--green)" : "var(--red)", fontWeight:600 }}>
												{won ? "W" : "L"}
											</span>
										</td>
										<td>
											<button type="button" className="btn btn-ghost" style={{ fontSize:11, padding:"2px 7px" }}
												onClick={() => setOpenBox(bs)}>
												Box Score
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</section>
		) : null}

		{openBox && collegeTid != null ? (
			<BoxScoreModal box={openBox} tid={collegeTid} onClose={() => setOpenBox(null)} />
		) : null}
	</>
	);
};
